import { useState, useEffect } from 'react'
import supabase from '../../lib/supabase'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'

const { FiUpload, FiFile, FiCheck, FiX, FiLoader, FiImage } = FiIcons

function FileUploader({ onFileUploaded, acceptedFileTypes = "*", maxSizeMB = 5 }) {
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(null)
  const [uploadedUrl, setUploadedUrl] = useState(null)
  const [existingImages, setExistingImages] = useState([])
  const [showExistingImages, setShowExistingImages] = useState(false)
  
  useEffect(() => {
    if (acceptedFileTypes.includes('image')) {
      fetchExistingImages()
    }
  }, [acceptedFileTypes])
  
  const fetchExistingImages = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('images')
        .list('uploads', {
          sortBy: { column: 'created_at', order: 'desc' }
        })
      
      if (error) throw error
      
      // Filter out folders and only keep files
      const imageFiles = data.filter(file => 
        !file.metadata?.isFolder && 
        file.name && 
        (!file.metadata?.mimetype || file.metadata?.mimetype.startsWith('image/'))
      )
      
      // Get public URLs for each image
      const imagesWithUrls = await Promise.all(imageFiles.map(async (file) => {
        const { data } = supabase.storage
          .from('images')
          .getPublicUrl(`uploads/${file.name}`)
        
        return {
          ...file,
          publicUrl: data.publicUrl
        }
      }))
      
      setExistingImages(imagesWithUrls)
      console.log('Fetched images:', imagesWithUrls)
    } catch (error) {
      console.error('Error fetching existing images:', error)
    }
  }
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    setError(null)
    
    if (!selectedFile) {
      setFile(null)
      return
    }
    
    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024
    if (selectedFile.size > maxSizeBytes) {
      setError(`Το αρχείο είναι πολύ μεγάλο. Μέγιστο μέγεθος: ${maxSizeMB}MB`)
      setFile(null)
      return
    }
    
    // Check file type if specified
    if (acceptedFileTypes !== "*") {
      const fileType = selectedFile.type
      const acceptedTypes = acceptedFileTypes.split(',').map(type => type.trim())
      
      if (!acceptedTypes.some(type => fileType.startsWith(type) || type === "*")) {
        setError(`Μη αποδεκτός τύπος αρχείου. Αποδεκτοί τύποι: ${acceptedFileTypes}`)
        setFile(null)
        return
      }
    }
    
    setFile(selectedFile)
  }
  
  const uploadFile = async () => {
    if (!file) return
    
    try {
      setUploading(true)
      setProgress(0)
      setError(null)
      
      // Generate a unique file name
      const fileExt = file.name.split('.').pop()
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`
      const filePath = `uploads/${fileName}`
      
      console.log('Uploading file:', file.name, 'to path:', filePath)
      
      // Upload to Storage
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type
        })
      
      if (uploadError) {
        console.error('Upload error:', uploadError)
        throw uploadError
      }
      
      console.log('File uploaded successfully')
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath)
      
      console.log('Public URL:', publicUrl)
      setUploadedUrl(publicUrl)
      
      try {
        // Register the file in our database
        const { error: dbError } = await supabase.rpc('register_uploaded_file', {
          filename: file.name,
          filepath: filePath,
          filesize: file.size,
          filetype: file.type
        })
        
        if (dbError) {
          console.error('Error registering file in database:', dbError)
          // We don't throw here because the file is already uploaded
        }
      } catch (dbError) {
        console.error('Exception registering file:', dbError)
        // Continue despite DB error since the file is uploaded
      }
      
      // Notify parent component
      if (onFileUploaded) {
        onFileUploaded(publicUrl)
      }
      
      // Refresh the list of existing images
      if (acceptedFileTypes.includes('image')) {
        setTimeout(() => fetchExistingImages(), 1000) // Wait for storage to update
      }
      
      return publicUrl
    } catch (error) {
      console.error('Error uploading file:', error)
      setError('Σφάλμα κατά την μεταφόρτωση: ' + error.message)
      return null
    } finally {
      setUploading(false)
    }
  }
  
  const selectExistingImage = (url) => {
    setUploadedUrl(url)
    setShowExistingImages(false)
    
    // Notify parent component
    if (onFileUploaded) {
      onFileUploaded(url)
    }
  }
  
  const cancelUpload = () => {
    setFile(null)
    setProgress(0)
    setError(null)
  }
  
  return (
    <div className="mt-2">
      {!file && !uploadedUrl ? (
        <div>
          <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              <SafeIcon icon={FiUpload} className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                  <span>Επιλέξτε αρχείο</span>
                  <input 
                    id="file-upload" 
                    name="file-upload" 
                    type="file" 
                    className="sr-only"
                    accept={acceptedFileTypes}
                    onChange={handleFileChange}
                  />
                </label>
                <p className="pl-1">ή σύρετε και αφήστε</p>
              </div>
              <p className="text-xs text-gray-500">
                {acceptedFileTypes === "*" 
                  ? `Οποιοδήποτε αρχείο έως ${maxSizeMB}MB`
                  : `${acceptedFileTypes.replace('image/', '')} έως ${maxSizeMB}MB`}
              </p>
            </div>
          </div>
          
          {acceptedFileTypes.includes('image') && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => {
                  fetchExistingImages();
                  setShowExistingImages(!showExistingImages);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <SafeIcon icon={FiImage} className="mr-2 h-5 w-5" />
                {showExistingImages ? 'Κρύψε υπάρχουσες εικόνες' : 'Επίλεξε υπάρχουσα εικόνα'}
              </button>
              
              {showExistingImages && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 max-h-60 overflow-y-auto p-2 border rounded-md">
                  {existingImages.length > 0 ? (
                    existingImages.map((image, index) => (
                      <div 
                        key={index} 
                        className="relative cursor-pointer group"
                        onClick={() => selectExistingImage(image.publicUrl)}
                      >
                        <img 
                          src={image.publicUrl} 
                          alt={image.name || `Image ${index + 1}`} 
                          className="h-24 w-full object-cover rounded-md border border-gray-200 group-hover:border-blue-500"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/150?text=Image+Error';
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 rounded-md transition-opacity">
                          <SafeIcon icon={FiCheck} className="h-8 w-8 text-white" />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-4 text-center py-4 text-gray-500">
                      Δεν βρέθηκαν εικόνες. Ανεβάστε μία πρώτα.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ) : uploadedUrl ? (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Επιτυχής μεταφόρτωση</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>Το αρχείο μεταφορτώθηκε επιτυχώς.</p>
                {uploadedUrl && (
                  <div className="mt-2">
                    {acceptedFileTypes.includes('image') && (
                      <img 
                        src={uploadedUrl} 
                        alt="Uploaded" 
                        className="h-32 w-auto object-cover rounded-md"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/150?text=Image+Error';
                        }}
                      />
                    )}
                    <a 
                      href={uploadedUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="mt-2 inline-block font-medium underline"
                    >
                      Προβολή αρχείου
                    </a>
                  </div>
                )}
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <button
                    type="button"
                    onClick={() => {
                      setFile(null)
                      setUploadedUrl(null)
                    }}
                    className="bg-green-50 px-2 py-1.5 rounded-md text-sm font-medium text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-green-50 focus:ring-green-600"
                  >
                    Μεταφόρτωση νέου αρχείου
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <SafeIcon icon={FiFile} className="h-5 w-5 text-gray-400" />
              <span className="ml-2 text-sm text-gray-500 truncate max-w-xs">
                {file.name}
              </span>
            </div>
            <button
              type="button"
              onClick={cancelUpload}
              className="ml-4 flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-500"
            >
              <SafeIcon icon={FiX} className="h-5 w-5" />
            </button>
          </div>
          
          {progress > 0 && (
            <div className="mt-2">
              <div className="bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="mt-1 text-xs text-gray-500 text-right">{progress}%</p>
            </div>
          )}
          
          {error && (
            <div className="mt-2 text-sm text-red-600">
              {error}
            </div>
          )}
          
          <div className="mt-4">
            <button
              type="button"
              onClick={uploadFile}
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <SafeIcon icon={FiLoader} className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Μεταφόρτωση...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiUpload} className="-ml-1 mr-2 h-4 w-4" />
                  Μεταφόρτωση
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default FileUploader